import { defineMessages } from "react-intl";

import {
	ascitesMessages,
	dialysisAtLeastTwicePerWeekMessages,
	encephalopathyGradeMessages,
	validator
} from "lib/build/forms/ltx/scores";

import { FieldData, FieldsetData } from "src/features/forms";
import { genericErrorMessages } from "src/messages/generic";
import { LeafNode } from "src/modules/shared/form-explorer";

const messages = defineMessages({
	scores: {
		id: "form.scores",
		defaultMessage: "Score számítások"
	},
	creatinine: {
		id: "form.scores.creatinine",
		defaultMessage: "Kreatinin"
	},
	bilirubinTotal: {
		id: "form.scores.bilirubinTotal",
		defaultMessage: "Bilirubin (össz.)"
	},
	albumin: {
		id: "form.scores.albumin",
		defaultMessage: "Albumin"
	},
	inr: {
		id: "form.scores.inr",
		defaultMessage: "INR"
	},
	natrium: {
		id: "form.scores.natrium",
		defaultMessage: "Nátrium"
	},
	dialysisAtLeastTwicePerWeek: {
		id: "form.scores.dialysisAtLeastTwicePerWeek",
		defaultMessage: "Dialízis (legalább heti 2×)"
	},
	ascites: {
		id: "form.scores.ascites",
		defaultMessage: "Aszitesz"
	},
	encephalopathyGrade: {
		id: "form.scores.encephalopathyGrade",
		defaultMessage: "Encephalopathia fokozata"
	},
	meldScore: {
		id: "form.scores.meldScore",
		defaultMessage: "MELD score"
	},
	meldNaScore: {
		id: "form.scores.meldNaScore",
		defaultMessage: "MELD-Na score"
	},
	childPughScore: {
		id: "form.scores.childPughScore",
		defaultMessage: "Child-Pugh score"
	},
	childPughClass: {
		id: "form.scores.childPughClass",
		defaultMessage: "Child-Pugh osztály"
	}
});

const fields: { [key: string]: FieldData } = {
	creatinine: {
		type: "field",
		path: ["creatinine"],
		label: messages.creatinine,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	bilirubinTotal: {
		type: "field",
		path: ["bilirubinTotal"],
		label: messages.bilirubinTotal,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	albumin: {
		type: "field",
		path: ["albumin"],
		label: messages.albumin,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	inr: {
		type: "field",
		path: ["inr"],
		label: messages.inr,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	natrium: {
		type: "field",
		path: ["natrium"],
		label: messages.natrium,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	dialysisAtLeastTwicePerWeek: {
		type: "field",
		path: ["dialysisAtLeastTwicePerWeek"],
		label: messages.dialysisAtLeastTwicePerWeek,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: dialysisAtLeastTwicePerWeekMessages }
		}
	},
	ascites: {
		type: "field",
		path: ["ascites"],
		label: messages.ascites,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: ascitesMessages } }
	},
	encephalopathyGrade: {
		type: "field",
		path: ["encephalopathyGrade"],
		label: messages.encephalopathyGrade,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: encephalopathyGradeMessages }
		}
	},
	meldScore: {
		type: "field",
		path: ["meldScore"],
		label: messages.meldScore,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number", disabled: true } }
	},
	meldNaScore: {
		type: "field",
		path: ["meldNaScore"],
		label: messages.meldNaScore,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number", disabled: true } }
	},
	childPughScore: {
		type: "field",
		path: ["childPughScore"],
		label: messages.childPughScore,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number", disabled: true } }
	},
	childPughClass: {
		type: "field",
		path: ["childPughClass"],
		label: messages.childPughClass,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "text", disabled: true } }
	}
};

const fieldset: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 6, field: fields.creatinine },
				{ width: 6, field: fields.bilirubinTotal }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.albumin },
				{ width: 6, field: fields.inr }
			]
		},
		{ columns: [{ width: 6, field: fields.natrium }] },
		{
			columns: [
				{ width: 6, field: fields.dialysisAtLeastTwicePerWeek },
				{ width: 6, field: fields.ascites }
			]
		},
		{ columns: [{ width: 12, field: fields.encephalopathyGrade }] },
		{
			columns: [
				{ width: 6, field: fields.meldScore },
				{ width: 6, field: fields.meldNaScore }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.childPughScore },
				{ width: 6, field: fields.childPughClass }
			]
		}
	]
};

export const node: LeafNode = {
	id: "scores",
	value: {
		title: messages.scores,
		validator,
		fieldset,
		applyCurrentDate: true,
		defaults: [
			{
				path: ["creatinine"],
				query: { interpretation: { vocab: "ltx", code: "creatinine" } }
			},
			{
				path: ["bilirubinTotal"],
				query: {
					interpretation: { vocab: "ltx", code: "bilirubin_total" }
				}
			},
			{
				path: ["albumin"],
				query: {
					interpretation: { vocab: "ltx", code: "serum_albumin" }
				}
			},
			{
				path: ["inr"],
				query: { interpretation: { vocab: "ltx", code: "inr" } }
			},
			{
				path: ["natrium"],
				query: { interpretation: { vocab: "ltx", code: "natrium" } }
			},
			{
				path: ["encephalopathyGrade"],
				query: {
					interpretation: {
						vocab: "ltx",
						code: "encephalopathy_grade"
					}
				}
			}
		]
	}
};
